import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Layers } from 'lucide-react'
import { ROUTE_ORDERS } from '@klear/constants'
import type { Payable } from '@klear/payable/payable'
import { payableTypeLabel } from '@klear/payable/list-display'
import { isEventLine, linkedEventIdsFrom } from '@klear/payment/line-item'
import { useOperationals } from '@klear/ui/hooks/use-operationals'
import { Badge } from '@klear/ui/components/badge'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@klear/ui/components/hover-card'

type PayableLinkedTradesProps = {
  payable: Payable
  urlPrefixApp: string
}

function tradeIdsFrom(payable: Payable): number[] {
  const ids = payable.lineItems
    .filter(isEventLine)
    .map((line) => line.orderId)
    .filter((id): id is number => id != null && id > 0)
  return [...new Set(ids)]
}

export function PayableLinkedTrades({ payable, urlPrefixApp }: PayableLinkedTradesProps) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const tradeIds = useMemo(() => tradeIdsFrom(payable), [payable])
  const eventIds = useMemo(() => linkedEventIdsFrom(payable.lineItems), [payable])
  const { operationals } = useOperationals({ orderEventId: eventIds }, { enabled: isOpen && eventIds.length > 0 })

  const trades = useMemo(() => {
    const byOrder = new Map<number, { display: string; eventCount: number }>()
    for (const operational of operationals) {
      const entry = byOrder.get(operational.orderId) ?? { display: operational.orderIdDisplay, eventCount: 0 }
      byOrder.set(operational.orderId, { display: entry.display, eventCount: entry.eventCount + 1 })
    }
    return [...byOrder.entries()].map(([orderId, entry]) => ({ orderId, ...entry }))
  }, [operationals])

  if (tradeIds.length < 2) {
    if (payable.orderId != null && payable.orderIdDisplay) {
      return (
        <Link
          to={`${urlPrefixApp}/${ROUTE_ORDERS}/${payable.orderId}`}
          className="hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          <Badge variant="secondary" className="text-xs hover:bg-blue-50 hover:text-blue-700">
            {payable.orderIdDisplay}
          </Badge>
        </Link>
      )
    }
    return (
      <Badge variant="secondary" className="text-xs">
        {payableTypeLabel(payable)}
      </Badge>
    )
  }

  return (
    <HoverCard openDelay={120} onOpenChange={setIsOpen}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          className="rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label={`Covers ${tradeIds.length} trades`}
        >
          <Badge variant="secondary" className="gap-1 whitespace-nowrap text-xs hover:bg-blue-50 hover:text-blue-700">
            <Layers className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
            {tradeIds.length} trades
          </Badge>
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-72 p-0">
        <div className="border-b border-border px-3 py-2">
          <p className="text-sm font-medium text-foreground">Covers {tradeIds.length} trades</p>
          <p className="text-xs text-muted-foreground">Each event stays tracked on its own trade.</p>
        </div>
        <ul className="max-h-64 overflow-y-auto py-1">
          {trades.length === 0 ? (
            <li className="px-3 py-1.5 text-sm text-muted-foreground">Loading trades…</li>
          ) : (
            trades.map((trade) => (
              <li key={trade.orderId}>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`${urlPrefixApp}/${ROUTE_ORDERS}/${trade.orderId}`)
                  }}
                  className="flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                >
                  <span className="min-w-0 truncate font-medium">{trade.display}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {trade.eventCount} {trade.eventCount === 1 ? 'event' : 'events'}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      </HoverCardContent>
    </HoverCard>
  )
}
